import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ocFetch, ocSetSessionToken } from "@/lib/oc-api";

const OC_REMEMBER_KEY = "oc_remembered_email";

const SSO_ERROR_MESSAGES: Record<string, string> = {
  tenant_not_found: "Tenant not found. Contact your administrator.",
  tenant_inactive: "Your organization's account is inactive. Contact your administrator.",
  sso_not_configured: "SSO is not configured for this organization.",
  discovery_failed: "Failed to reach the identity provider. Try again later.",
  token_exchange_failed: "Token exchange with identity provider failed. Try again.",
  no_id_token: "No identity token received from the identity provider.",
  invalid_id_token: "Identity token is invalid. Contact your administrator.",
  nonce_mismatch: "Security check failed. Please try signing in again.",
  no_email_in_token: "No email address in the identity token.",
  user_not_found: "Your account was not found in this organization. Contact your administrator.",
  missing_token: "SSO did not return a session token.",
};

export function OcLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => localStorage.getItem(OC_REMEMBER_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(OC_REMEMBER_KEY));
  const [error, setError] = useState<string | null>(() => {
    const ssoError = searchParams.get("sso_error");
    if (!ssoError) return null;
    return SSO_ERROR_MESSAGES[ssoError] ?? `SSO error: ${ssoError}`;
  });
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await ocFetch<{ token: string; user: { email: string; name: string; role: string } }>(
        "/tenant/auth/login",
        { method: "POST", body: JSON.stringify({ email, password }) }
      );
      if (rememberMe) {
        localStorage.setItem(OC_REMEMBER_KEY, email);
      } else {
        localStorage.removeItem(OC_REMEMBER_KEY);
      }
      ocSetSessionToken(res.token);
      navigate("/dashboard", { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Login failed";
      setError(msg.includes("401") ? "Invalid email or password." : msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 [color-scheme:light]">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <svg viewBox="0 0 48 54" fill="none" className="w-9 h-9 flex-shrink-0" aria-hidden="true">
              <path d="M24 2 L44 10 L44 26 C44 37 34 45 24 50 C14 45 4 37 4 26 L4 10 Z"
                    fill="none" stroke="#00D4FF" strokeWidth="2.5"/>
              <path d="M13 26 L20 33 L35 18" stroke="#00D4FF" strokeWidth="3.5"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="text-left">
              <div className="text-base font-bold text-slate-900 leading-tight">OnboardConnect</div>
              <div className="text-[10px] font-medium text-oc-teal-600 tracking-widest uppercase leading-tight">for Slate CRM</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Tenant Administration Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="oc-login-email" className="block text-sm font-medium text-slate-700">Email</label>
            <input
              id="oc-login-email"
              type="email"
              required
              autoFocus={!email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-oc-teal-500 focus:border-transparent"
              placeholder="you@institution.edu"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="oc-login-password" className="block text-sm font-medium text-slate-700">Password</label>
            <input
              id="oc-login-password"
              type="password"
              required
              autoFocus={!!email}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-oc-teal-500 focus:border-transparent"
              placeholder="••••••••"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-oc-teal-600 focus:ring-oc-teal-500"
            />
            <span className="text-sm text-slate-600">Remember me</span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-oc-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-oc-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

      </div>
    </div>
  );
}
